import React, { useRef, useState, useEffect } from 'react';
import { FaPlay, FaPause, FaMoon, FaHeart, FaShare, FaExternalLinkAlt } from 'react-icons/fa';
import { usePlayerStore } from '../../stores/playerStore';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { radioService } from '../../api/radioService';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import { useSleepCountdown } from '../../hooks/useSleepCountdown';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import SleepTimer from '../SleepTimer';
import { logger } from '../../utils/logger';
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid';
import styled from 'styled-components';
import ShareModal from '../ShareModal';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=400&auto=format';

const VolumeControl = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 20px;

  /* Create a larger invisible hover area */
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 120px;
    z-index: 1;
  }

  .volume-slider-container {
    position: absolute;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    height: 100px;
    width: 20px;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    z-index: 2;
  }

  &:hover .volume-slider-container,
  .volume-slider-container.active {
    display: flex;
  }

  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 80px;
    height: 4px;
    background: transparent;
    transform: rotate(-90deg);
    transform-origin: center;
    position: relative;
    cursor: pointer;
    z-index: 2;
    will-change: transform;
    transform-style: preserve-3d;
    backface-visibility: hidden;
  }

  input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    will-change: background;
  }

  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #FEAA2D;
    cursor: pointer;
    margin-top: -4px;
    border: 2px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 10px rgba(254, 170, 45, 0.3);
    transition: transform 0.1s ease;
    will-change: transform;
    transform: translateZ(0);
  }

  input[type="range"]::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    box-shadow: 0 0 15px rgba(254, 170, 45, 0.5);
  }

  /* Fairy theme styles */
  .theme-fairy & input[type="range"]::-webkit-slider-thumb {
    background: #B8A5D1;
    box-shadow: 0 0 10px rgba(184, 165, 209, 0.3);
  }

  .theme-fairy & input[type="range"]::-webkit-slider-thumb:hover {
    box-shadow: 0 0 15px rgba(184, 165, 209, 0.5);
  }
`;

const DesktopPlayerBar: React.FC = () => {
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSleepTimer, setShowSleepTimer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    currentStation,
    isPlaying,
    volume,
    setIsPlaying,
    setVolume,
    sleepTimer,
  } = usePlayerStore();

  // All playback lives in the shared audio engine. This component used to own a
  // `new Audio()` of its own, alongside an identical copy in MobilePlayer — which
  // meant two elements once the viewport crossed the breakpoint.
  const {
    isBuffering,
    isConnected,
    connectionQuality,
    streamError,
    unplayableReason,
    externalUrl,
  } = useAudioPlayer();

  const sleepCountdown = useSleepCountdown();

  const { addFavorite, removeFavorite, isFavorite } = useFavoritesStore();
  const { showToast } = useToast();
  const { mode } = useTheme();
  const isFairyTheme = mode === 'fairy';

  // Report the click once per station, not on every retry.
  useEffect(() => {
    if (!currentStation) return;
    radioService
      .reportStationClick(currentStation.stationuuid)
      .catch(error => logger.error('Failed to report station click:', error));
  }, [currentStation?.stationuuid]);

  const handlePlayPause = () => {
    if (!currentStation) return;
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    const target = e.target;

    // Update visual immediately without debounce
    const percent = newVolume * 100;
    const color = isFairyTheme ? '#B8A5D1' : '#FEAA2D';
    target.style.background = `linear-gradient(to top, ${color} 0%, ${color} ${percent}%, rgba(255, 255, 255, 0.1) ${percent}%, rgba(255, 255, 255, 0.1) 100%)`;

    setVolume(newVolume);
  };

  const handleVolumeClick = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  const handleFavoriteToggle = () => {
    if (!currentStation) return;

    if (isFavorite(currentStation.stationuuid)) {
      removeFavorite(currentStation);
      showToast('Removed from favorites', 'info');
    } else {
      addFavorite(currentStation);
      showToast('Added to favorites', 'success');
    }
  };

  // Handle click outside volume slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentStation) return null;

  const isFavorited = isFavorite(currentStation.stationuuid);

  const handleShare = () => {
    setShowShareModal(true);
  };

  return (
    <div className={`fixed bottom-0 left-0 right-0 h-20 ${isFairyTheme
      ? 'bg-purple-900/95 border-t border-purple-500/20'
      : mode === 'neon'
        ? 'bg-black/95 border-t border-[#00FF00]/20'
        : 'bg-gray-900/95'
      } backdrop-blur-md z-50`}>
      {/* Progress Bar with error state */}
      <div className="h-0.5 w-full bg-gray-700">
        <div
          className={`h-full transition-all duration-500 ${streamError ? 'bg-red-500' :
            isBuffering ? 'bg-yellow-500 animate-pulse' :
              connectionQuality === 'good' ? 'bg-green-500' :
                connectionQuality === 'fair' ? 'bg-yellow-500' :
                  'bg-red-500'
            }`}
          style={{
            width: isBuffering ? '60%' : '100%',
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Station Info */}
          <div className="flex items-center space-x-4 flex-1 min-w-0 max-w-[40%]">
            <img
              src={currentStation.favicon || FALLBACK_IMAGE}
              alt={currentStation.name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-lg truncate leading-tight" title={currentStation.name}>
                {currentStation.name}
              </h3>
              <div className="flex items-center space-x-2 text-sm text-gray-300 min-w-0">
                {currentStation.countrycode && (
                  <img
                    src={`https://flagcdn.com/w20/${currentStation.countrycode.toLowerCase()}.png`}
                    alt={currentStation.country}
                    className="w-4 h-4 rounded-sm flex-shrink-0"
                  />
                )}
                <span className="truncate max-w-[120px]" title={currentStation.country}>{currentStation.country}</span>
                {isBuffering && !streamError && <span className="text-yellow-500 flex-shrink-0">(Buffering...)</span>}
                {streamError && (
                  <>
                    <span className="text-red-400 flex-shrink-0">
                      {unplayableReason === 'mixed-content'
                        ? 'Insecure stream — blocked by your browser'
                        : 'Stream unavailable'}
                    </span>
                    {externalUrl && (
                      <a
                        href={externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline flex-shrink-0"
                        title="Streams that a secure page can't embed still play in their own tab"
                      >
                        <FaExternalLinkAlt size={10} />
                        Open in new tab
                      </a>
                    )}
                  </>
                )}
                {isConnected && !isBuffering && !streamError && (
                  <>
                    <span className="text-green-500 flex-shrink-0">•</span>
                    <span className="text-green-500 animate-pulse flex-shrink-0">LIVE</span>
                    <span className="text-gray-400 flex-shrink-0">|</span>
                    <span className={`flex-shrink-0 ${connectionQuality === 'good' ? 'text-green-500' :
                      connectionQuality === 'fair' ? 'text-yellow-500' :
                        'text-red-500'
                      }`}>
                      {connectionQuality.toUpperCase()}
                    </span>
                    {/* Connection Quality Indicator */}
                    <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-900 ${!isConnected
                      ? 'bg-red-500'
                      : connectionQuality === 'good'
                        ? 'bg-green-500'
                        : connectionQuality === 'fair'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`} />
                    {currentStation.clickcount && currentStation.clickcount > 0 && (
                      <>
                        <span className="text-gray-400 flex-shrink-0">|</span>
                        <span className="text-primary flex-shrink-0">
                          {currentStation.clickcount.toLocaleString()} listeners
                          {currentStation.clicktrend && currentStation.clicktrend > 0 && (
                            <span className="text-green-500 ml-1">↑</span>
                          )}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePlayPause}
              disabled={streamError}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors
                ${isFairyTheme ? 'bg-purple-500 hover:bg-purple-600'
                  : mode === 'neon'
                    ? 'bg-[#00FF00] hover:bg-[#00FF00]/90'
                    : 'bg-primary hover:bg-primary/90'}
                ${streamError ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={streamError ? 'Stream unavailable' : isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <FaPause size={20} className={mode === 'neon' ? 'text-black' : ''} />
              ) : (
                <FaPlay size={20} className={mode === 'neon' ? 'text-black' : ''} />
              )}
            </button>

            <VolumeControl ref={volumeControlRef}>
              <button
                onClick={handleVolumeClick}
                className={`text-white hover:text-gray-300 transition-colors ${mode === 'neon' ? 'text-[#00FF00] hover:text-[#00FF00]/80' : ''
                  }`}
              >
                {volume === 0 ? (
                  <SpeakerXMarkIcon className="w-6 h-6" />
                ) : (
                  <SpeakerWaveIcon className="w-6 h-6" />
                )}
              </button>
              <div className={`volume-slider-container ${showVolumeSlider ? 'active' : ''}`}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="volume-slider-vertical"
                  style={{
                    background: `linear-gradient(to top, 
                      ${isFairyTheme ? '#B8A5D1' : '#FEAA2D'} ${volume * 100}%, 
                      rgba(255, 255, 255, 0.1) ${volume * 100}%
                    )`
                  }}
                />
              </div>
            </VolumeControl>

            <button
              onClick={handleFavoriteToggle}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors
                ${isFavorited ? 'text-red-500' : 'text-gray-300 hover:text-white'}`}
            >
              <FaHeart size={20} />
            </button>

            <button
              onClick={() => setShowSleepTimer(true)}
              title={sleepCountdown ? `Sleeping in ${sleepCountdown}` : 'Sleep timer'}
              className={`h-10 rounded-full flex items-center justify-center gap-1.5 transition-colors
                ${sleepCountdown ? 'px-3' : 'w-10'}
                ${isFairyTheme ? 'bg-purple-500 hover:bg-purple-600'
                  : mode === 'neon'
                    ? 'bg-[#00FF00] hover:bg-[#00FF00]/90'
                    : 'bg-primary hover:bg-primary/90'}
                ${sleepTimer && sleepTimer > 0 ? 'animate-pulse' : ''}`}
            >
              <FaMoon size={16} className={mode === 'neon' ? 'text-black' : ''} />
              {sleepCountdown && (
                <span className={`text-xs font-semibold tabular-nums ${mode === 'neon' ? 'text-black' : ''}`}>
                  {sleepCountdown}
                </span>
              )}
            </button>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className={`p-2 rounded-full transition-colors ${isFairyTheme
                ? 'hover:bg-purple-800/60'
                : mode === 'neon'
                  ? 'hover:bg-[#00FF00]/20'
                  : 'hover:bg-gray-700'
                }`}
              title="Share"
            >
              <FaShare size={16} />
            </button>
          </div>
        </div>

        {/* Sleep Timer Modal */}
        {showSleepTimer && (
          <SleepTimer onClose={() => setShowSleepTimer(false)} />
        )}

        {/* Share Modal */}
        {showShareModal && (
          <ShareModal station={currentStation} onClose={() => setShowShareModal(false)} />
        )}
      </div>
    </div>
  );
};

export default DesktopPlayerBar; 